import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Tag, Plus, Trash2, X, Pencil } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/utils'
import { useAuth } from '../context/AuthContext'
import CsvControls from '../components/CsvControls'
import { DiscountTargetPicker, type Target } from '../components/DiscountTargetPicker'

interface DiscountCode {
  id: string
  code: string
  type: string
  value: number
  user_id: string | null
  user_name: string | null
  user_phone: string | null
  game_play_id: string | null
  starts_at: string | null
  valid_until: string | null
  used_at: string | null
  order_id: string | null
  max_uses: number | null
  times_used: number
  per_customer_limit: number | null
  min_subtotal_fils: number | null
  min_quantity: number | null
  first_order_only: number
  targets?: { kind: string; ref: string; label: string }[]
  created_at: string
}

function formatDiscount(code: DiscountCode) {
  if (code.type === 'percentage') return `${code.value}% off`
  if (code.type === 'fixed') return `JD ${(code.value / 1000).toFixed(3)} off`
  return 'Free delivery'
}

function CreateCodeDialog({ onClose, editing }: { onClose: () => void; editing?: DiscountCode }) {
  const qc = useQueryClient()
  const [codeInput, setCodeInput] = useState(editing?.code ?? '')
  const [type, setType] = useState(editing?.type ?? 'percentage')
  const [value, setValue] = useState(editing ? String(editing.value) : '10')
  const [startsAt, setStartsAt] = useState(editing?.starts_at?.slice(0, 10) ?? '')
  const [validUntil, setValidUntil] = useState(editing?.valid_until?.slice(0, 10) ?? '')
  const [maxUses, setMaxUses] = useState(editing?.max_uses != null ? String(editing.max_uses) : '')
  // Public codes default to once-per-customer (the common promo case); ops
  // can raise it to N or clear it for no per-customer cap.
  const [perCustomer, setPerCustomer] = useState(
    editing ? (editing.per_customer_limit != null ? String(editing.per_customer_limit) : '') : '1',
  )
  const [minSpend, setMinSpend] = useState(editing?.min_subtotal_fils != null ? (editing.min_subtotal_fils / 1000).toString() : '')
  const [minQty, setMinQty] = useState(editing?.min_quantity != null ? String(editing.min_quantity) : '')
  const [firstOrderOnly, setFirstOrderOnly] = useState(editing?.first_order_only === 1)
  const [error, setError] = useState('')

  // Scoping (1b): whole order (default) vs specific products / brands /
  // categories, collected as { kind, ref } chips via DiscountTargetPicker.
  const [scopeMode, setScopeMode] = useState<'all' | 'items'>(editing?.targets && editing.targets.length > 0 ? 'items' : 'all')
  const [targets, setTargets] = useState<Target[]>((editing?.targets ?? []) as Target[])

  const save = useMutation({
    mutationFn: (body: object) => (editing
      ? api.patch(`/discounts/${editing.id}`, body)
      : api.post('/discounts', body)).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['discount-codes'] })
      onClose()
    },
    onError: (e: any) => setError(e.response?.data?.message ?? 'Failed to save code'),
  })

  const handleSubmit = (ev: React.FormEvent) => {
    ev.preventDefault()
    save.mutate({
      code: codeInput.trim().toUpperCase() || undefined,
      type,
      value: parseInt(value || '0', 10),
      starts_at: startsAt || undefined,
      valid_until: validUntil || undefined,
      max_uses: maxUses ? parseInt(maxUses, 10) : undefined,
      per_customer_limit: perCustomer ? parseInt(perCustomer, 10) : undefined,
      min_subtotal_fils: minSpend ? Math.round(parseFloat(minSpend) * 1000) : undefined,
      min_quantity: minQty ? parseInt(minQty, 10) : undefined,
      first_order_only: firstOrderOnly,
      targets: scopeMode === 'items' && targets.length > 0
        ? targets.map(t => ({ kind: t.kind, ref: t.ref }))
        : undefined,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Tag size={18} className="text-[#c41e3a]" />
            <h2 className="text-white font-semibold">{editing ? 'Edit Discount Code' : 'Create Discount Code'}</h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Code <span className="text-gray-600">(leave blank to auto-generate)</span></label>
            <input
              type="text"
              value={codeInput}
              onChange={e => setCodeInput(e.target.value.toUpperCase())}
              placeholder="e.g. SUMMER20"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono tracking-wide placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#c41e3a]"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Type</label>
              <select
                value={type}
                onChange={e => setType(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#c41e3a]"
              >
                <option value="percentage">% Off</option>
                <option value="fixed">Fixed Amount (fils)</option>
                <option value="free_delivery">Free Delivery</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                Value {type === 'percentage' ? '(%)' : type === 'fixed' ? '(fils)' : '(ignored)'}
              </label>
              <input
                type="number"
                min="0"
                max={type === 'percentage' ? 100 : undefined}
                value={value}
                onChange={e => setValue(e.target.value)}
                disabled={type === 'free_delivery'}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#c41e3a] disabled:opacity-40"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Starts <span className="text-gray-600">(optional)</span></label>
              <input
                type="date"
                value={startsAt}
                onChange={e => setStartsAt(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#c41e3a]"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Expires <span className="text-gray-600">(optional)</span></label>
              <input
                type="date"
                value={validUntil}
                onChange={e => setValidUntil(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#c41e3a]"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Max uses (total) <span className="text-gray-600">(optional)</span></label>
              <input
                type="number"
                min="1"
                value={maxUses}
                onChange={e => setMaxUses(e.target.value)}
                placeholder="Unlimited"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#c41e3a]"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Per customer <span className="text-gray-600">(blank = no limit)</span></label>
              <input
                type="number"
                min="1"
                value={perCustomer}
                onChange={e => setPerCustomer(e.target.value)}
                placeholder="No limit"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#c41e3a]"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Min spend (JD) <span className="text-gray-600">(optional)</span></label>
              <input
                type="number"
                min="0"
                step="0.001"
                value={minSpend}
                onChange={e => setMinSpend(e.target.value)}
                placeholder="None"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#c41e3a]"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Min items <span className="text-gray-600">(optional)</span></label>
              <input
                type="number"
                min="1"
                value={minQty}
                onChange={e => setMinQty(e.target.value)}
                placeholder="None"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#c41e3a]"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={firstOrderOnly}
              onChange={e => setFirstOrderOnly(e.target.checked)}
              className="rounded border-white/20 bg-white/5 text-[#c41e3a] focus:ring-[#c41e3a]"
            />
            <span className="text-sm text-white">First order only</span>
            <span className="text-xs text-gray-500">(only the customer's first online order; in-store sales don't count)</span>
          </label>

          <div className="border border-white/10 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">Applies to</span>
              <div className="flex gap-1">
                {(['all', 'items'] as const).map(m => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setScopeMode(m)}
                    className={`px-2 py-1 rounded text-xs ${scopeMode === m ? 'bg-[#c41e3a] text-white' : 'bg-white/5 text-gray-400'}`}
                  >
                    {m === 'all' ? 'Whole order' : 'Specific items'}
                  </button>
                ))}
              </div>
            </div>

            {scopeMode === 'items' && (
              <div className="space-y-1.5">
                <DiscountTargetPicker targets={targets} onChange={setTargets} />
                <p className="text-[11px] text-gray-600">The discount only comes off matching items. The charge is correct; the in-app preview for scoped codes needs the next app release.</p>
              </div>
            )}
          </div>

          {error && <p className="text-red-400 text-sm">{error}</p>}

          <button
            type="submit"
            disabled={save.isPending}
            className="w-full px-4 py-2.5 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830] disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : editing ? 'Save Changes' : 'Create Code'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default function DiscountCodes() {
  const { user } = useAuth()
  const canCreate = user?.role === 'super_admin' || user?.role === 'operations'
  const canDelete = user?.role === 'super_admin'
  const qc = useQueryClient()

  const [showCreate, setShowCreate] = useState(false)
  const [editingCode, setEditingCode] = useState<DiscountCode | null>(null)
  const [sourceFilter, setSourceFilter] = useState<'all' | 'admin' | 'game'>('all')
  const [search, setSearch] = useState('')

  const { data, isLoading } = useQuery<{ codes: DiscountCode[] }>({
    queryKey: ['discount-codes', sourceFilter, search],
    queryFn: () => {
      const p = new URLSearchParams()
      if (sourceFilter !== 'all') p.set('source', sourceFilter)
      if (search.trim()) p.set('search', search.trim())
      const qs = p.toString()
      return api.get(`/discounts${qs ? `?${qs}` : ''}`).then(r => r.data)
    },
  })

  const deleteCode = useMutation({
    mutationFn: (id: string) => api.delete(`/discounts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['discount-codes'] }),
  })

  const codes = data?.codes ?? []

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-lg font-semibold text-white">Discount Codes</h1>
        {canCreate && (
          <div className="flex items-center gap-2">
            <CsvControls
              basePath="/discount-codes"
              templateFilename="discount-codes-template.csv"
              exportFilename={`discount-codes-export-${new Date().toISOString().slice(0, 10)}.csv`}
              label="discount code"
              onImported={() => qc.invalidateQueries({ queryKey: ['discount-codes'] })}
            />
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830]"
            >
              <Plus size={15} />
              New Code
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-5">
        {(['all', 'admin', 'game'] as const).map(f => (
          <button
            key={f}
            onClick={() => setSourceFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              sourceFilter === f
                ? 'bg-[#c41e3a]/20 text-[#c41e3a]'
                : 'bg-white/5 text-gray-400 hover:text-white'
            }`}
          >
            {f === 'all' ? 'All' : f === 'admin' ? 'Admin-Created' : 'Game Prizes'}
          </button>
        ))}
        <input
          value={search}
          onChange={e => setSearch(e.target.value.toUpperCase())}
          placeholder="Search code…"
          className="ml-auto w-48 bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-1.5 text-sm text-white font-mono placeholder-gray-600 focus:outline-none focus:border-[#c41e3a]"
        />
      </div>

      {isLoading ? (
        <div className="text-gray-500 text-sm">Loading…</div>
      ) : codes.length === 0 ? (
        <div className="text-center py-16 text-gray-600">
          <Tag size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No discount codes yet</p>
        </div>
      ) : (
        <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-600 border-b border-[#2e2e2e]">
                <th className="px-4 py-3 text-left font-medium">Code</th>
                <th className="px-4 py-3 text-left font-medium">Discount</th>
                <th className="px-4 py-3 text-left font-medium">Customer</th>
                <th className="px-4 py-3 text-left font-medium">Uses</th>
                <th className="px-4 py-3 text-left font-medium">Expires</th>
                <th className="px-4 py-3 text-left font-medium">Rules</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-left font-medium">Created</th>
                {(canCreate || canDelete) && <th className="px-4 py-3 w-16" />}
              </tr>
            </thead>
            <tbody>
              {codes.map(c => (
                <tr key={c.id} className="border-b border-[#222]">
                  <td className="px-4 py-3">
                    <span className="font-mono text-white text-xs tracking-wide bg-white/5 px-2 py-1 rounded">{c.code}</span>
                    {c.game_play_id && (
                      <span className="ml-2 text-[10px] text-purple-400 bg-purple-400/10 px-1.5 py-0.5 rounded">Game</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-300">{formatDiscount(c)}</td>
                  <td className="px-4 py-3">
                    {c.user_name ? (
                      <>
                        <p className="text-white text-xs">{c.user_name}</p>
                        <p className="text-gray-500 text-xs">{c.user_phone}</p>
                      </>
                    ) : (
                      <span className="text-gray-600 text-xs">Public</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {c.times_used}{c.max_uses ? `/${c.max_uses}` : ''}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                    {c.valid_until ? formatDateTime(c.valid_until) : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                    {[
                      c.first_order_only ? 'first order only' : null,
                      c.per_customer_limit ? `${c.per_customer_limit}/cust` : null,
                      c.min_subtotal_fils ? `min JD ${(c.min_subtotal_fils / 1000).toFixed(3)}` : null,
                      c.min_quantity ? `min ${c.min_quantity} items` : null,
                      c.starts_at ? `from ${c.starts_at.slice(0, 10)}` : null,
                      c.targets && c.targets.length > 0 ? `scoped (${c.targets.length})` : null,
                    ].filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td className="px-4 py-3">
                    {(() => {
                      const isPersonal = c.user_id !== null || c.game_play_id !== null
                      const exhausted = (isPersonal && c.used_at !== null) || (c.max_uses !== null && c.times_used >= c.max_uses)
                      return exhausted ? (
                        <span className="inline-flex px-2 py-0.5 rounded-full bg-gray-500/15 text-gray-400 text-xs">Used</span>
                      ) : (
                        <span className="inline-flex px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 text-xs">Active</span>
                      )
                    })()}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{formatDateTime(c.created_at)}</td>
                  {(canCreate || canDelete) && (
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {canCreate && !c.game_play_id && (
                          <button
                            onClick={() => setEditingCode(c)}
                            title="Edit code"
                            className="text-gray-600 hover:text-white transition-colors"
                          >
                            <Pencil size={14} />
                          </button>
                        )}
                        {canDelete && !c.used_at && (
                          <button
                            onClick={() => deleteCode.mutate(c.id)}
                            disabled={deleteCode.isPending}
                            title="Delete code"
                            className="text-gray-600 hover:text-red-400 transition-colors disabled:opacity-40"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(showCreate || editingCode) && (
        <CreateCodeDialog
          editing={editingCode ?? undefined}
          onClose={() => { setShowCreate(false); setEditingCode(null) }}
        />
      )}
    </div>
  )
}
